/**
 * JKL PDP Promo Banner
 * Promo area for Product Detail Page: incentive bar + promotional slider.
 *
 * Swiper configuration:
 * - Incentive bar: 1 message at a time, fade, autoplay, dots
 * - Promo slider: 1 slide at a time, dots, optional autoplay (merchant setting)
 * - rewind instead of loop: no cloned slides, so theme editor block selection
 *   and slide links keep working
 * - Autoplay is disabled for users who prefer reduced motion
 */

import Swiper from './swiper-bundle.esm.browser.min.js';

(function() {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initIncentiveSwiper(root) {
    const swiperEl = root.querySelector('[data-pdp-incentive-swiper]');
    if (!swiperEl || swiperEl.swiper) return;

    const slidesCount = swiperEl.querySelectorAll('.swiper-slide').length;
    const isSlider = slidesCount > 1;
    const speed = parseInt(root.dataset.incentiveSpeed, 10) || 4;

    new Swiper(swiperEl, {
      slidesPerView: 1,
      effect: 'fade',
      fadeEffect: { crossFade: true },
      speed: 500,
      rewind: isSlider,
      allowTouchMove: isSlider,
      autoplay: isSlider && !reducedMotion ? {
        delay: speed * 1000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      } : false,
      pagination: isSlider ? {
        el: root.querySelector('[data-pdp-incentive-pagination]'),
        clickable: true,
      } : false,
      a11y: { enabled: true },
    });
  }

  function initPromoSwiper(root) {
    const swiperEl = root.querySelector('[data-pdp-promo-swiper]');
    if (!swiperEl || swiperEl.swiper) return;

    const slidesCount = swiperEl.querySelectorAll('.swiper-slide').length;
    const isSlider = slidesCount > 1;
    const autoplay = root.dataset.autoplay === 'true';
    const speed = parseInt(root.dataset.autoplaySpeed, 10) || 5;

    new Swiper(swiperEl, {
      slidesPerView: 1,
      spaceBetween: 16,
      speed: 500,
      rewind: isSlider,
      allowTouchMove: isSlider,
      grabCursor: isSlider,
      mousewheel: isSlider ? { forceToAxis: true } : false,
      autoplay: isSlider && autoplay && !reducedMotion ? {
        delay: speed * 1000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      } : false,
      pagination: isSlider ? {
        el: root.querySelector('[data-pdp-promo-pagination]'),
        clickable: true,
      } : false,
      a11y: {
        enabled: true,
        prevSlideMessage: 'Previous slide',
        nextSlideMessage: 'Next slide',
      },
    });
  }

  function init() {
    document.querySelectorAll('[data-pdp-promo]').forEach((root) => {
      initIncentiveSwiper(root);
      initPromoSwiper(root);
    });
  }

  // Theme editor: show the selected incentive message / promo slide
  function getBlockSwiper(event) {
    const slide = event.target.closest('.swiper-slide');
    const swiperEl = slide && slide.closest('.swiper');
    if (!swiperEl || !swiperEl.swiper) return null;
    return { swiper: swiperEl.swiper, index: Array.from(swiperEl.swiper.slides).indexOf(slide) };
  }

  document.addEventListener('shopify:block:select', (event) => {
    const target = getBlockSwiper(event);
    if (!target) return;
    if (target.swiper.autoplay) target.swiper.autoplay.stop();
    target.swiper.slideTo(target.index);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const target = getBlockSwiper(event);
    if (target && target.swiper.params.autoplay && target.swiper.params.autoplay.enabled) {
      target.swiper.autoplay.start();
    }
  });

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('[data-pdp-promo]')) {
      setTimeout(init, 100);
    }
  });
})();
